import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { creatinineDisplay, creatinineUnitLabel, CREAT_US_TO_SI, weightDisplay, weightUnitLabel } from "../context/UnitsContext";
import { STAGE_LABELS, computeIRIS } from "./iris";

function labRowsForExport(labs, units) {
  return [...labs]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((l) => ({
      date: l.date,
      creatinine:
        l.creatinine_mg_dl == null
          ? ""
          : units === "SI"
            ? (l.creatinine_mg_dl * CREAT_US_TO_SI).toFixed(1)
            : Number(l.creatinine_mg_dl).toFixed(2),
      sdma: l.sdma_ug_dl ?? "",
      usg: l.usg ?? "",
      upc: l.upc ?? "",
      bp: l.bp_systolic ?? "",
      notes: l.notes || "",
    }));
}

export function exportLabsCSV(cat, labs, units) {
  const header = [
    "Date",
    `Creatinine (${creatinineUnitLabel(units)})`,
    "SDMA (µg/dL)",
    "USG",
    "UPC",
    "SBP (mmHg)",
    "Notes",
  ];
  const rows = labRowsForExport(labs, units).map((r) => [
    r.date,
    r.creatinine,
    r.sdma,
    r.usg,
    r.upc,
    r.bp,
    (r.notes || "").replace(/[\r\n]+/g, " "),
  ]);
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${cat.name.replace(/\s+/g, "_")}_labs.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function exportLabsPDF(cat, labs, units) {
  const iris = computeIRIS(labs);
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const marginX = 48;
  let y = 56;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(`${cat.name} — Lab Report`, marginX, y);
  y += 22;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const meta = [
    cat.breed && `Breed: ${cat.breed}`,
    cat.sex && `Sex: ${cat.sex}`,
    cat.date_of_birth && `DOB: ${cat.date_of_birth}`,
    cat.weight_kg != null && `Weight: ${weightDisplay(cat.weight_kg, units)} ${weightUnitLabel(units)}`,
    `Report date: ${new Date().toISOString().slice(0, 10)}`,
  ]
    .filter(Boolean)
    .join("   ·   ");
  doc.setTextColor(90);
  doc.text(meta, marginX, y);
  doc.setTextColor(0);
  y += 20;

  // IRIS block
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("IRIS 2023 Assessment", marginX, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  if (iris.ready) {
    const lines = [
      `Stage: ${STAGE_LABELS[iris.stage]}`,
      `Stable creatinine: ${creatinineDisplay(iris.stableCreatinine, units)} ${creatinineUnitLabel(units)}`,
      iris.latestSdma != null ? `Latest SDMA: ${iris.latestSdma} µg/dL${iris.sdmaBumped ? " (bumped stage above creatinine)" : ""}` : "SDMA: not measured",
      iris.proteinuria
        ? `Proteinuria substage: ${iris.proteinuria.code} — ${iris.proteinuria.label} (UPC ${iris.proteinuria.upc})`
        : "Proteinuria substage: UPC not measured",
      iris.bp
        ? `Blood pressure substage: ${iris.bp.code} — ${iris.bp.label} (SBP ${iris.bp.sbp} mmHg)`
        : "Blood pressure substage: SBP not measured",
    ];
    lines.forEach((l) => {
      doc.text(l, marginX, y);
      y += 14;
    });
  } else {
    doc.text(iris.reason || "Insufficient data to stage.", marginX, y);
    y += 14;
  }
  y += 8;

  // Labs table
  const rows = labRowsForExport(labs, units).map((r) => [
    r.date,
    r.creatinine,
    r.sdma,
    r.usg,
    r.upc,
    r.bp,
    r.notes,
  ]);
  autoTable(doc, {
    startY: y,
    head: [
      [
        "Date",
        `Creat (${creatinineUnitLabel(units)})`,
        "SDMA (µg/dL)",
        "USG",
        "UPC",
        "SBP",
        "Notes",
      ],
    ],
    body: rows,
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [27, 20, 109], textColor: 255 },
    alternateRowStyles: { fillColor: [237, 240, 249] },
    margin: { left: marginX, right: marginX },
  });

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(
    "Generated by Cat Kidney Compass. Educational use only — clinical decisions belong to your veterinarian.",
    marginX,
    doc.internal.pageSize.getHeight() - 24,
  );

  doc.save(`${cat.name.replace(/\s+/g, "_")}_report.pdf`);
}
